import PropTypes from 'prop-types'
import { ascend, equals, isEmpty, pick, prop, sortWith } from 'ramda'
import React, { Component, PureComponent } from 'react'
import { Button, Div, H4, Icon, Input, P, TextLoader } from '@driftt/tide-core-legacy'
import app from 'containers/app'
import ConnectButton from '../../components/ConnectButton'
import withNotifications from 'components/shared/notifications'
import * as GoogleAnalyticsActions from 'app/modules/googleAnalytics/actions'
import {
  enabledEventMappingsSelector,
  individualEventsEnabled,
  settingsLoaded,
  trackingName
} from 'app/modules/googleAnalytics/selectors'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { fetchIntegrationStates } from 'app/modules/integrations/actions'

import './style.styl'

const CONNECTED_MESSAGE = 'Google Analytics was successfully connected!'
const SAVED_MESSAGE = 'Your Google Analytics integration settings were successfully updated!'
const TRACKING_NAME_HELP_LINK = 'https://developers.google.com/analytics/devguides/collection/analyticsjs/creating-trackers'

const TRACKING_NAME_DESCRIPTION = `
Add your tracking ID to start tracking Drift events using Google Analytics!
If you have more than one, specify which ID you want to use here.
`

const sortSupportedEventsByLabel = sortWith([ascend(prop('label'))])

const SUPPORTED_EVENTS = Object.freeze(sortSupportedEventsByLabel([
  // Please keep the `label` field in sentence case as preferred by design.
  // If you an add an option here, ensure that it's also updated in the sdk-js
  { key: 'startConversation', label: 'Chat started' },
  { key: 'emailCapture', label: 'Email captured' },
  { key: 'conversation:buttonClicked', label: 'Button clicked' },
  { key: 'conversation:firstInteraction', label: 'First interaction' },
  { key: 'message', label: 'Message received' },
  { key: 'message:sent', label: 'Message sent' },
  { key: 'phoneCapture', label: 'Phone number captured' },
  { key: 'scheduling:meetingBooked', label: 'Meeting booked' },
  { key: 'conversation:playbookClicked', label: 'Playbook clicked' },
  { key: 'conversation:playbookFired', label: 'Playbook fired' },
  { key: 'conversation:playbookDismissed', label: 'Playbook dismissed' }
]))

const createDefaultEventMappings = (value) => {
  const eventMappings = {}
  SUPPORTED_EVENTS.forEach(({ key }) => {
    eventMappings[key] = value
  })
  return eventMappings
}

const GoogleAnalyticsTrackingSettings = ({ trackingName, onInputChange }) => {
  return (
    <Div className="google-analytics-tracking-container">
      <H4>Tracking Info</H4>
      <P className="google-analytics-tracking-description">
        {TRACKING_NAME_DESCRIPTION}
        <a className="google-analytics-description-link" href={TRACKING_NAME_HELP_LINK}> Learn More</a>
      </P>
      <label htmlFor="google-analytics-tracking-input">Tracking ID</label>
      <Input
        className="google-analytics-tracking-input"
        id="google-analytics-tracking-input"
        type="text"
        placeholder="UA-XXXXXX-X"
        value={trackingName || ''}
        onChange={onInputChange}
      />
    </Div>
  )
}

class GoogleAnalyticsEventSettings extends PureComponent {
  static propTypes = {
    individualEventsEnabled: PropTypes.bool,
    enabledEventMappings: PropTypes.object,
    onChange: PropTypes.func
  }
  
  static defaultProps = {
    enabledEventMappings: {}
  }

  state = {
    individualEventsEnabled: this.props.individualEventsEnabled || false,
    enabledEventMappings: this.props.enabledEventMappings
  }

  componentWillReceiveProps(nextProps) {
    const nextState = {}
    
    if (this.props.individualEventsEnabled !== nextProps.individualEventsEnabled) {
      nextState.individualEventsEnabled = nextProps.individualEventsEnabled
    }

    if (!equals(this.props.enabledEventMappings, nextProps.enabledEventMappings)) {
      nextState.enabledEventMappings = nextProps.enabledEventMappings
    }

    if (!isEmpty(nextState)) {
      this.setState(nextState)
    }
  }

  onStateChange = () => {
    if (this.props.onChange) {
      this.props.onChange(pick(
        ['individualEventsEnabled', 'enabledEventMappings'],
        this.state
      ))
    }
  }

  onSendEventChoiceClick = choice => () => {
    const individualEventsEnabled = choice === 'events_individual'
    if (this.state.individualEventsEnabled !== individualEventsEnabled) {
      this.setState({
        individualEventsEnabled,
        enabledEventMappings: isEmpty(this.state.enabledEventMappings) ? createDefaultEventMappings(true) : this.state.enabledEventMappings
      }, this.onStateChange)
    }
  }

  onCheckboxChange = key => () => {
    this.setState(state => ({
      enabledEventMappings: {
        ...state.enabledEventMappings,
        [key]: !state.enabledEventMappings[key]
      }
    }), this.onStateChange)
  }

  renderEventsChecklist() {
    const { enabledEventMappings } = this.state
    return (
      <Div className="google-analytics-events-checklist">
        {SUPPORTED_EVENTS.map(({ key, label }, index) => (
          <Input
            key={`checkbox_${index}`}
            type="checkbox"
            label={label}
            checked={enabledEventMappings[key]}
            onChange={this.onCheckboxChange(key)}
          />
        ))}
      </Div>
    )
  }

  renderRadio(id, labelText, checked) {
    return (
      <Div className="google-analytics-events-radio-container" onClick={this.onSendEventChoiceClick(id)}>
        <Input type="radio" name="events" id={id} checked={checked} />
        <label htmlFor={id}>{labelText}</label>
      </Div>
    )
  }

  render() {
    const { individualEventsEnabled } = this.state

    return (
      <Div className="google-analytics-events-container">
        <H4>Drift <Icon name="arrow-2-right" width="14" /> Google Analytics Events</H4>
        <Div className="google-analytics-events-content">
          {this.renderRadio('events_all', 'Send all events', !individualEventsEnabled)}
          {this.renderRadio('events_individual', 'Only send specified events', individualEventsEnabled)}
          {individualEventsEnabled && this.renderEventsChecklist()}
        </Div>
      </Div>
    )
  }
}

class GoogleAnalyticsIntegrationPage extends Component {
  state = {
    individualEventsEnabled: this.props.individualEventsEnabled,
    enabledEventMappings: this.props.enabledEventMappings,
    trackingName: this.props.trackingName,
    canSave: false
  }

  componentDidMount() {
    this.props.actions.getGoogleAnalyticsSettings()
  }

  componentWillReceiveProps(nextProps) {
    const nextState = {}

    if (this.props.trackingName !== nextProps.trackingName) {
      nextState.trackingName = nextProps.trackingName
    }

    if (this.props.individualEventsEnabled !== nextProps.individualEventsEnabled) {
      nextState.individualEventsEnabled = nextProps.individualEventsEnabled
    }

    if (!equals(this.props.enabledEventMappings, nextProps.enabledEventMappings)) {
      nextState.enabledEventMappings = nextProps.enabledEventMappings
    }

    if (!isEmpty(nextState)) {
      this.setState(nextState)
    }
  }

  onConnectClicked = () => {
    this.patchGoogleAnalytics({ enabled: true, notificationMessage: CONNECTED_MESSAGE })
  }

  onSaveTracker = () => {
    this.patchGoogleAnalytics({ enabled: true, notificationMessage: SAVED_MESSAGE })
  }

  patchGoogleAnalytics({ enabled, notificationMessage }) {
    const payload = { enabled }
    const { enabledEventMappings, individualEventsEnabled, trackingName } = this.state

    if (trackingName) {
      payload.trackingName = trackingName
    }

    payload.enabledEventMappings = individualEventsEnabled ? enabledEventMappings : {}
    payload.individualEventsEnabled = individualEventsEnabled

    this.props.actions.patchGoogleAnalytics(payload)
    this.props.actions.fetchIntegrationStates()
    app.actions.NotificationActions.pushNotification({
      content: notificationMessage,
    })

    this.setState({ canSave: false })
  }

  handleInputChange = (event) => {
    this.setState({
      trackingName: event.target.value,
      canSave: true
    })
  }

  onEventSettingsChange = ({ individualEventsEnabled, enabledEventMappings }) => {
    this.setState({
      individualEventsEnabled,
      enabledEventMappings,
      canSave: true
    })
  }

  renderAdvancedOptionSectionBody = () => (
    <GoogleAnalyticsEventSettings
      enabledEventMappings={this.state.enabledEventMappings}
      individualEventsEnabled={this.state.individualEventsEnabled}
      onChange={this.onEventSettingsChange}
    />
  )

  renderContent() {
    const { isConnected, settingsLoaded } = this.props
    const {
      enabledEventMappings,
      individualEventsEnabled,
      trackingName,
      canSave
    } = this.state

    if (!isConnected) {
      return <ConnectButton onClick={this.onConnectClicked} />
    } else if (settingsLoaded) {
      return (
        <Div className="google-analytics-integration-settings">
          <GoogleAnalyticsTrackingSettings
            trackingName={trackingName}
            onInputChange={this.handleInputChange}
          />
          <GoogleAnalyticsEventSettings
            enabledEventMappings={enabledEventMappings}
            individualEventsEnabled={individualEventsEnabled}
            onChange={this.onEventSettingsChange}
          />
          {canSave && <Div className="google-analytics-save-button">
            <Button
              onClick={this.onSaveTracker}
              type="primary"
            >
              Save
            </Button>
          </Div>}
        </Div>
      )
    }
    else {
      return <TextLoader />
    }
  }

  render() {
    return (
      <Div className="google-analytics-integration-page">
        <P className="google-analytics-integration-page-description">
          Automatically send Drift data to Google Analytics
          to easily compare Drift to other sources
          and gain a better understanding of your website traffic!
          <a href="//help.drift.com/integrations/google-analytics-integration" target="_blank"> Learn More</a>
        </P>
        {this.renderContent()}
      </Div>
    )
  }
}

function mapStateToProps(state) {
  let enabledEventMappings = enabledEventMappingsSelector(state)
  if (enabledEventMappings) {
    enabledEventMappings = enabledEventMappings.toJS()
  }

  return {
    enabledEventMappings,
    individualEventsEnabled: individualEventsEnabled(state),
    settingsLoaded: settingsLoaded(state),
    trackingName: trackingName(state),
  }
}

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    getGoogleAnalyticsSettings: GoogleAnalyticsActions.getGoogleAnalyticsSettings,
    patchGoogleAnalytics: GoogleAnalyticsActions.patchGoogleAnalytics,
    fetchIntegrationStates
  }, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(
  withNotifications(GoogleAnalyticsIntegrationPage)
)



// WEBPACK FOOTER //
// ./src/scripts/components/Integration/pages/GoogleAnalyticsIntegrationPage/index.js